import promise from 'promise';

import api from 'app/shared/utils/api';
import Constants from 'app/shared/constants';
import ListingConversion from 'app/shared/model/ListingConversion';
import Properties from 'app/shared/model/Properties';
import { PropertiesSelectors } from 'app/shared/selectors';

// constants
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import StringConstants from 'app/shared/constants/StringConstants';

// models
import ErrorRMW from 'app/shared/model/ErrorRMW';
import ListingsCount from 'app/shared/model/ListingsCount';
import ListingInt from 'app/shared/model/ListingsInt';
import PropertyOverview from 'app/shared/model/PropertyOverview';
import ListingSourceResponse from 'app/shared/model/ListingSourceResponse';

// service
import appLogger from 'app/shared/loggers/appLogger';

// TODO: remove mock data
import {
    mockBaseListing,
    mockConnectedMF,
    mockConnectedSF,
    mockNotConnectedSF,
} from 'app/shared/containers/__mocks__/properties/listingsData';

// FIXME: remove debug flag after testing is complete
const DEBUG = false;

const fetchMockData = () => {
    const listings = [
        ...mockBaseListing.listings,
        ...mockConnectedMF.listings,
        ...mockConnectedSF.listings,
        ...mockNotConnectedSF.listings,
    ];

    const response = {
        ...mockBaseListing,
        listings,
    };

    return response;
};

const PropertiesActions = {
    // add boolean for restrictions here
    getListings(appendToListings = false, useV2API = false) {
        return (dispatch, getState) => {
            const params = PropertiesSelectors.makePropertiesFilters(getState());

            // TODO: handle mock response for v1 api
            if (DEBUG && useV2API) {
                console.info('[debug] USING MOCK DATA');

                return promise.resolve(
                    dispatch(PropertiesActions.updateListings(Properties(fetchMockData()), appendToListings))
                );
            }

            return dispatch(api.users.properties.listings(params, useV2API))
                .then((response = {}) => {
                    const { error: errorRMWModel, success } = response;

                    if (response && !success && errorRMWModel) {
                        return promise.reject(errorRMWModel);
                    }

                    if (response.success && response.response) {
                        const propertiesModel = Properties(response.response);

                        return dispatch(PropertiesActions.updateListings(propertiesModel, appendToListings));
                    }

                    return response;
                }).catch((errorRMWModel) => {
                    appLogger.logError({
                        message: errorRMWModel.message,
                        context: 'PropertiesActions.getListings',
                    });

                    return promise.reject(errorRMWModel);
                });
        };
    },
    getListingSources(listingAlias) {
        return dispatch => dispatch(api.users.properties.feedListingManagement.getListingSources({ listingAlias }))
            .then((response = {}) => {
                if (response.success === false) {
                    return promise.reject(response.error);
                }
                return new ListingSourceResponse(response.response);
            }).catch(err => promise.reject(err));
    },
    applicationsAccepted(propertyId) {
        return dispatch => dispatch(api.users.properties.applicationsAccepted(propertyId))
            .then((response = {}) => {
                if (response.success === false) {
                    return promise.reject(response.error);
                }
                return response;
            }).catch(err => promise.reject(err));
    },
    getListingsCount() {
        return dispatch => dispatch(api.users.properties.listingsCount())
            .then((response = {}) => {
                if (response.success && response.response) {
                    const listingsCountModel = ListingsCount(response.response);
                    return dispatch(PropertiesActions.updateListingsCount(listingsCountModel));
                }
                return response;
            });
    },

    // get interstitial (intent) listings
    getIntListings(type, params) {
        return dispatch => dispatch(api.users.properties.listingInt(type, params))
            .then((response) => {
                const { error: errorRMWModel, success } = response;

                if (response && !success && errorRMWModel) {
                    return promise.reject(errorRMWModel);
                }

                if (success && response.response) {
                    return ListingInt(response.response);
                }

                return response;
            })
            .catch((errorRMWModel) => {
                // dispatch
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'PropertiesActions.getIntListings',
                });

                return promise.reject(errorRMWModel);
            });
    },

    updateCurrentProperty(payload) {
        return dispatch => dispatch({
            type: Constants.UPDATE_CURRENT_PROPERTY,
            payload,
        });
    },
    updatePropertyType({ propertyId, propertyTypeCode }) {
        const propertyTypeData = {
            fieldName: 'propertyTypeCode',
            propertyTypeCode,
            propertyId,
        };

        return dispatch => dispatch(api.users.properties.edit(propertyTypeData))
            .then((response) => {
                const { error: errorRMWModel, success } = response;

                if (!success) {
                    return promise.reject(errorRMWModel);
                }

                return response;
            })
            .catch((errorRMWModel) => {
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'PropertiesActions.updatePropertyType',
                });

                return promise.reject(errorRMWModel);
            });
    },
    savePropertyInfo({
        fullAddress = '',
        unit = undefined,
        numUnits = '',
        propertyId = '',
        propertyTypeCode = '',
    } = {}) {
        const propertyType = PropertyConstants.PROPERTY_TYPES_BY_CODE[propertyTypeCode];

        return (dispatch) => {
            const addressData = {
                fullAddress,
                propertyId,
                unitNumber: unit,
            };
            // TODO: we really need to switch to the multi endpoint for this
            return dispatch(api.properties.address.updateAddress(addressData))
                .then((response) => {
                    let { error: errorRMWModel } = response;

                    if (response && !response.success) {
                        if (!errorRMWModel) {
                            errorRMWModel = {
                                data: '',
                                code: '',
                                message: 'Something went wrong - Error is undefined',
                            };
                        }
                        // dispatch
                        appLogger.logError({
                            message: errorRMWModel.message,
                            context: 'PropertiesActions.savePropertyInfo',
                        });

                        return promise.reject(errorRMWModel);
                    }

                    const {
                        street, city, state, zip, lat, lon,
                    } = response.response.addressDetails;

                    const propertyTypeData = {
                        fieldName: 'propertyTypeCode',
                        propertyTypeCode,
                        propertyId,
                    };

                    return dispatch(api.users.properties.edit(propertyTypeData))
                        .then(() => {
                            if (propertyType === PropertyConstants.PROPERTY_TYPES.COMMUNITY && numUnits !== '') {
                                const dataToSave = {
                                    fieldName: 'unitsInBuilding',
                                    unitsInBuilding: numUnits,
                                    propertyId,
                                };

                                return dispatch(api.users.properties.edit(dataToSave));
                            }
                        })
                        .then(() => {
                            const updatedPropertyData = {
                                propertyType,
                                street,
                                city,
                                state,
                                zip,
                                unit,
                                geo: {
                                    lat,
                                    lon,
                                },
                                numUnits,
                            };
                            return dispatch(PropertiesActions.updateCurrentProperty(updatedPropertyData));
                        });
                })
                .catch(errorRMWModel => promise.reject(errorRMWModel));
        };
    },
    deleteSpecialOffer(queryParams) {
        return dispatch => dispatch(api.properties.specialOffers.delete(queryParams))
            .then((response) => {
                const { error: errorRMWModel, success } = response;

                if (response && !success && errorRMWModel) {
                    return promise.reject(errorRMWModel);
                }

                return response.success;
            })
            .catch((errorRMWModel) => {
                // dispatch
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'PropertiesActions.deleteSpecialOffer',
                });

                return promise.reject(errorRMWModel);
            });
    },
    createOrUpdateSpecialOffer(bodyParams, queryParams) {
        return (dispatch, getUser) => dispatch(api.properties.specialOffers.createOrUpdate(bodyParams, queryParams))
            .then((response) => {
                if (response.success) {
                    return response.response;
                }
                return promise.reject({
                    errors: response.errors,
                });
            })
            .catch(({ errors }) => {
                errors.forEach((error) => {
                    const message = `Special Offer Error @ ${error.name} where code is ${error.errorCode}`;

                    appLogger.logError({
                        message,
                        context: 'PropertiesActions.createOrUpdateSpecialOffer',
                        user: getUser.user,
                    });
                });

                return promise.reject(ErrorRMW({
                    data: {
                        errors,
                    },
                }));
            });
    },
    updateFilters(data) {
        return (dispatch) => {
            dispatch({
                type: Constants.UPDATE_PROPERTIES_FILTERS,
                payload: data,
            });
        };
    },
    updateListingsCount(listingsCountModel) {
        return dispatch => dispatch({
            type: Constants.UPDATE_PROPERTIES_COUNT,
            payload: listingsCountModel,
        });
    },
    updateListings(listingsModel, appendToListings = false) {
        return dispatch => dispatch({
            type: Constants.UPDATE_PROPERTIES,
            payload: {
                listingsModel,
                appendToListings,
            },
        });
    },
    getIsConnectedExperience(propertyId) {
        return dispatch => dispatch(api.users.properties.isConnectedExperience(propertyId))
            .then((result) => {
                const { error: errorRMWModel, success, response } = result;
                const { isConnectedExperience } = response;

                if (success) {
                    // FIND: update listing isConnectedExperience
                    dispatch(PropertiesActions.updateCurrentProperty({
                        isConnectedExperience,
                    }));

                    return isConnectedExperience;
                }

                return promise.reject(errorRMWModel);
            }).catch((errorRMWModel) => {
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'PropertiesActions.getIsConnectedExperience',
                });

                return promise.reject(errorRMWModel);
            });
    },
    getOverview(propertyId) {
        return dispatch => dispatch(api.users.properties.getOverview(propertyId))
            .then((result) => {
                const { error: errorRMWModel, success, response } = result;

                if (success) {
                    const propertyOverview = PropertyOverview(response);

                    return dispatch({
                        type: Constants.UPDATE_PROPERTY_OVERVIEW,
                        payload: propertyOverview,
                    });
                }

                return promise.reject(errorRMWModel);
            }).catch((errorRMWModel) => {
                const errorToReturn = { ...errorRMWModel };
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'PropertiesActions.getOverview',
                });

                if (errorRMWModel.code === StringConstants.MISSING_PARAM) {
                    delete errorToReturn.message;
                }

                return promise.reject(errorToReturn);
            });
    },
    getListingConversionStatus() {
        return dispatch => dispatch(api.users.properties.feedToManual.statuses())
            .then((response = {}) => {
                const { error: errorRMWModel, success } = response;

                if (response && !success && errorRMWModel) {
                    return promise.reject(errorRMWModel);
                }

                const listingConversion = new ListingConversion(response);

                dispatch({
                    type: Constants.UPDATE_PROPERTIES_CONVERSION,
                    payload: listingConversion,
                });
                return promise.resolve(listingConversion);
            }).catch((errorRMWModel) => {
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'PropertiesActions.getListingConversionStatus',
                });
                return promise.reject(errorRMWModel.message);
            });
    },
};

export default PropertiesActions;
